// @file The store for handling the guided templates discussion board on surface
import { captureFetchException } from '@@/bits/error_tracker'
import PadletApi from '@@/surface/padlet_api'
import type { GuidedDiscussionQuestion, GuidedDiscussionQuestionsResponse } from '@@/types'
import type { JsonAPIResource, JsonAPIResponse } from '@padlet/arvo'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export enum GenerationStatus {
  Initial = 'Initial',
  Generating = 'Generating',
  Generated = 'Generated',
  Errored = 'Errored',
}

export enum GuidedSubpanel {
  Generate = 'generate',
  Configure = 'configure',
  Post = 'post',
  Share = 'share',
  Embed = 'embed',
}

export const useSurfaceGuidedTemplateDiscussionBoardStore = defineStore(
  'surfaceGuidedTemplateDiscussionBoardStore',
  () => {
    const activePanel = ref<GuidedSubpanel | null>(GuidedSubpanel.Generate)
    function setActivePanel(panel: GuidedSubpanel): void {
      activePanel.value = panel
    }

    const discussionTopic = ref('')
    function setDiscussionTopic(topic: string): void {
      discussionTopic.value = topic
    }

    const discussionGrades = ref<string[]>([])
    function updateDiscussionGrades(grade: string): void {
      if (discussionGrades.value.includes(grade)) {
        discussionGrades.value = discussionGrades.value.filter((item) => item !== grade)
      } else {
        discussionGrades.value = [...discussionGrades.value, grade]
      }
    }

    const discussionPrompt = ref('')
    function setDiscussionPrompt(prompt: string): void {
      discussionPrompt.value = prompt
    }
    const discussionStyle = ref('')
    function setDiscussionStyle(type: string): void {
      discussionStyle.value = type
    }

    const discussionQuestions = ref<GuidedDiscussionQuestion[] | null>()
    const discussionQuestionsStatus = ref<GenerationStatus>(GenerationStatus.Initial)
    const isGeneratingDiscussionQuestions = computed(
      () => discussionQuestionsStatus.value === GenerationStatus.Generating,
    )
    const isDiscussionQuestionsGenerated = computed(
      () => discussionQuestionsStatus.value === GenerationStatus.Generated,
    )

    async function generateDiscussionQuestions(): Promise<void> {
      try {
        discussionQuestionsStatus.value = GenerationStatus.Generating
        const response: JsonAPIResponse<GuidedDiscussionQuestionsResponse> =
          await PadletApi.GuidedTemplates.generateDiscussionQuestions(discussionTopic.value, discussionGrades.value)

        discussionQuestions.value = (
          response.data as JsonAPIResource<GuidedDiscussionQuestionsResponse>
        ).attributes.questions
        discussionQuestionsStatus.value = GenerationStatus.Generated
      } catch (e) {
        discussionQuestionsStatus.value = GenerationStatus.Errored
        captureFetchException(e, { source: 'SurfaceOnboardingGuidedPanelGenerateDiscussionQuestions' })
      }
    }

    return {
      activePanel,
      setActivePanel,

      discussionTopic,
      setDiscussionTopic,

      discussionGrades,
      updateDiscussionGrades,

      discussionPrompt,
      setDiscussionPrompt,

      discussionStyle,
      setDiscussionStyle,

      discussionQuestions,
      discussionQuestionsStatus,
      isGeneratingDiscussionQuestions,
      isDiscussionQuestionsGenerated,
      generateDiscussionQuestions,
    }
  },
)
